import { useMemo } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { motion } from 'framer-motion';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';

import { cn } from 'components/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from 'components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from 'components/ui/tooltip';

import { ISetupChecklistCategory } from '../types';

import { SetupChecklistTask } from './Task';

interface ISetupChecklistCategoryProps {
  category: ISetupChecklistCategory;
}

export const SetupChecklistCategory: React.FC<ISetupChecklistCategoryProps> = ({
  category
}) => {
  const { selectedCategory, setSelectedCategory } = useChecklistState();

  const { title, tasks, status, tooltip } = category;
  const isLocked = status === 'locked';

  const isOpen = useMemo(
    () => !isLocked && selectedCategory === title,
    [selectedCategory]
  );

  const textColorClass = useMemo(() => {
    if (status === 'done' || status === 'pending') return 'text-indigo-500';
    if (isOpen) return 'text-cardForeground';
    return 'text-base-mutedForeground';
  }, [status, isOpen]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <div className="w-full category-container">
            <Collapsible
              open={isOpen}
              onOpenChange={(isOpen) => {
                if (!isLocked) {
                  setSelectedCategory(isOpen ? title : '');
                }
              }}
              className={cn(textColorClass)}
            >
              <CollapsibleTrigger
                className={cn(
                  'flex justify-between items-center w-full',
                  isLocked && 'cursor-default'
                )}
              >
                <div
                  className={cn('text-sm font-semibold leading-5 font-sans')}
                >
                  {title}
                </div>
                <div className="flex items-center gap-3">
                  {status === 'pending' && (
                    <div className="text-base-mutedForeground text-xs italic">
                      Pending
                    </div>
                  )}
                  {isLocked ? (
                    <FontAwesomeIcon
                      icon={`fa-solid fa-lock`}
                      className="text-xs"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={`fa-solid ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                      className="text-sm"
                    />
                  )}
                </div>
              </CollapsibleTrigger>
              <motion.div
                layout
                initial={{ height: 0 }}
                animate={{ height: isOpen ? 'auto' : 0 }}
                transition={{ duration: 0.1, ease: 'easeInOut' }}
              >
                <CollapsibleContent>
                  {tasks.map((task) => (
                    <SetupChecklistTask key={task.title} task={task} />
                  ))}
                </CollapsibleContent>
              </motion.div>
            </Collapsible>
          </div>
        </TooltipTrigger>
        {tooltip && (
          <TooltipContent
            side="bottom"
            align="center"
            className="rounded-md border border-base-popover bg-base-popover shadow-md py-[6px] px-[12px]"
          >
            {tooltip}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};
